import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { submitContact } from '../../lib/api';
import { withTimeout } from '../../lib/utils';
import { Input } from '../ui/input';

export const IRP_URL = 'https://a7-mock-interview.vercel.app/login';

// Backends can take a while to wake up from a cold start — don't make the
// visitor wait more than this for a response before redirecting anyway.
const SUBMIT_TIMEOUT_MS = 4000;

export default function IrpLeadModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', course: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const contactPromise = submitContact({
      name: form.name,
      email: form.email,
      phone: form.phone,
      course: form.course,
      message: `IRP enquiry — interested course: ${form.course}`,
      source: 'homepage_irp',
      page: 'homepage_hero_irp',
    });

    const result = await withTimeout(contactPromise, SUBMIT_TIMEOUT_MS);
    setLoading(false);
    if (!result.timedOut && result.error) {
      setError('Something went wrong. Please try again.');
      return;
    }
    window.open(IRP_URL, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F1F3D]/70 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E1E7EF]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#585858]">Interview Readiness Platform</p>
            <h2 className="font-heading font-bold text-lg text-[#1F1F1F]">Start Your IRP Journey</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F0F0F0] hover:bg-[#E0E0E0] flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-[#585858]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-[#585858]">Share your details and we'll take you straight to the mock interview platform.</p>
          <div>
            <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Full Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Your full name"
              className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Email *</label>
            <Input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
              placeholder="your@email.com"
              className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Phone Number *</label>
            <Input
              type="tel"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              required
              placeholder="+91 XXXXX XXXXX"
              className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Which course are you interested in? *</label>
            <Input
              value={form.course}
              onChange={e => setForm({ ...form, course: e.target.value })}
              required
              placeholder="e.g. Data Science & Gen AI"
              className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full text-sm font-semibold py-3 rounded-md text-white bg-[#0056D2] hover:bg-[#003E99] flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {loading ? 'Submitting...' : 'Continue to IRP'}
          </button>
        </form>
      </div>
    </div>
  );
}
